import { useEffect, useState } from 'react';
import { Alert, App, Form, Modal } from 'antd';
import type { AdminLocation } from '@goelectric/shared-types';
import { useAllies } from '@/features/aliados/hooks/useAllies';
import { locationApi } from '../api/locationApi';
import { describeLocationApiError } from '../utils/locationErrors';
import { ALLY_OPTIONS_LIMIT, buildUpdateBody, type LocationFormState } from '../utils/locationForm';
import { LocationAddressFields } from './LocationAddressFields';
import { LocationGeoFields } from './LocationGeoFields';
import { LocationIdentityFields } from './LocationIdentityFields';
import { LocationMapPicker } from './LocationMapPicker';

interface LocationFormModalProps {
  open: boolean;
  /** Locación precargada que se edita (PATCH). */
  location: AdminLocation;
  onClose: () => void;
  /** Refresca el detalle tras un guardado exitoso. */
  onSaved: () => void;
}

/**
 * Edición de Locación (TG-123, US-2.2) en un modal de un solo formulario.
 *
 * Desde TG-123 el **alta** vive en el wizard (`CreateLocationPage`); este modal quedó solo
 * para editar (PATCH). Reusa los grupos de campos del wizard (`LocationIdentityFields`,
 * `LocationAddressFields`, `LocationGeoFields`) para no duplicar los `Form.Item`. El error
 * se muestra con el `message` real del backend (ADR 0021).
 */
export function LocationFormModal({ open, location, onClose, onSaved }: LocationFormModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<LocationFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allies = useAllies({ limit: ALLY_OPTIONS_LIMIT });

  // Precarga al abrir.
  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    form.resetFields();
    form.setFieldsValue({
      name: location.name,
      address: location.address,
      postalCode: location.postalCode,
      city: location.city,
      state: location.state,
      latitude: location.coordinates?.latitude,
      longitude: location.coordinates?.longitude,
      allyId: location.ally?.id,
    });
  }, [open, location, form]);

  const handleFinish = async (values: LocationFormState) => {
    setSubmitting(true);
    setError(null);
    try {
      await locationApi.update(location.id, buildUpdateBody(values));
      message.success('Ubicación actualizada.');
      onSaved();
      onClose();
    } catch (caught) {
      setError(describeLocationApiError(caught, 'No se pudo actualizar la ubicación.').message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Editar ubicación"
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={720}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo actualizar la ubicación"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <LocationIdentityFields allies={allies.data} alliesLoading={allies.loading} />
        <LocationAddressFields />
        {/* Mismo orden que el paso 3 del wizard: mapa arriba, inputs abajo. El modal se monta
            sólo al abrirse (`destroyOnHidden`), así que el mapa arranca visible. */}
        <LocationMapPicker />
        <LocationGeoFields isEdit />
      </Form>
    </Modal>
  );
}
